import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useVideoManager } from "../../../contexts/VideoManagerContext";
import { selectIsAudioEnabled } from "../../../app/actions/audio/audioSlice";
import { useHlsPlayer, isHlsSource } from "../../../hooks/useHlsPlayer";
// import SimpleVideo from "../../../components/video/SimpleVideo";

const FullCardStyled = styled.div`
  width: 100%;
  padding: 24px 0px 0px;
  max-height: 100dvh;
  overflow-y: hidden;

  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background-color: #fff;

  /* Card drop shadow */
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  

`;

const HalfCardStyled = styled.div`
  width: 100%;
  padding: 24px 0px 0px;
  min-height: 40dvh;
  overflow-y: hidden;

  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background-color: #fff;

  /* Card drop shadow */
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);

  @media (width > 960px) {
    width: calc(100% / 2 - 16px / 2);
  }
`;

const HalfCardGrayStyled = styled.div`
  width: 100%;
  padding: 32px 0px 0px;
  min-height: 40dvh;
  overflow-y: hidden;

  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background-color: #f0f0f0;

  /* Card drop shadow */
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);

  @media (width > 960px) {
    width: calc(100% / 2 - 16px / 2);
  }
`;

const VideoStyled = styled(motion.video)`
  width: min(100%, 1140px);

  border-radius: 0px 0px 12px 12px;

  &:hover {
    cursor: pointer;
  }
  
  /* モバイルでの選択を無効化 */
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;

  /* video要素のスタイル最適化 */
  & video {
    object-fit: contain !important;
    /* ハードウェアアクセラレーションを強制 */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    will-change: transform;
    /* iOS Safari対応 */
    -webkit-transform-style: preserve-3d;
    -webkit-backface-visibility: hidden;
  }
`;


// アイコン
const IconContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;

  background: rgba(255, 255, 255, 0.8);

  color: #000;
  border-radius: 4px;
  border: 1px solid #f0f0f0;

  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);

  font-size: 13px;
  line-height: 1;
  padding: 8px 12px;
  white-space: normal;
  outline: 0;
  transition-property: transform, visibility, opacity;
  transition: all 0.3s ease;
  
  /* モバイルでの選択を無効化 */
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;

  &:hover {
    color: #0076a5;
    scale: 1.05;
  }
`;

// 音声コントロールアイコン（右下）
const AudioControlContainer = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;

  background: rgba(255, 255, 255, 0.3);

  color: var(--soremo-light-gray);
  border-radius: 50%;

  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);

  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  transition: all 0.3s ease;
  
  /* モバイルでの選択を無効化 */
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;

  &:hover {
    scale: 1.1;
  }
`;

/* アイコン自体のスタイル例 */
const IconButton = styled.div.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
  cursor: pointer;
  color: #009ace;
  
  /* モバイルでの選択を無効化 */
  user-select: none;
  -webkit-user-select: none;
  
  /* テキスト部分も選択無効化 */
  & span {
    user-select: none;
    -webkit-user-select: none;
  }
`;

const AudioIcon = styled.div.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
  color: var(--soremo-light-gray);
  
  /* モバイルでの選択を無効化 */
  user-select: none;
  -webkit-user-select: none;
`;



const OsCard = ({ title, children, size = "full", src, StyledWrapper }) => {
  const isAudioEnabled = useSelector(selectIsAudioEnabled);
  const isMobile = useSelector((state) => state.device.isMobile);
  const isIPhone = useSelector((state) => state.device.isIPhone);
  
  const [pausedByClick, setPausedByClick] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true); // 個別の動画ミュート状態（true = ミュート、false = 音声ON）
  const [hasUserInteracted, setHasUserInteracted] = useState(!isIPhone); // iPhoneのみfalse、他はtrue
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const videoId = useRef(`video-${Math.random()}`).current;
  
  // HLSソースかどうかを判定
  const isHls = isHlsSource(src);
  
  // HLSプレイヤーフックを使用（HLSソースの場合のみ有効化）
  useHlsPlayer(src, videoRef, isHls);
  
  const { 
    activeVideoId,
    hoveredVideoId,
    registerVideo, 
    unregisterVideo, 
    playVideo, 
    pauseVideo,
    setVideoHovered
  } = useVideoManager();

  // ビデオ要素を登録
  useEffect(() => {
    if (videoRef.current) {
      registerVideo(videoId, videoRef.current);
    }
    return () => {
      // アンマウント時に動画を停止
      if (activeVideoId === videoId) {
        pauseVideo(videoId);
      }
      unregisterVideo(videoId);
    };
  }, [videoId, activeVideoId, registerVideo, unregisterVideo, pauseVideo]);

  // Intersection Observerをセットアップ
  useEffect(() => {
    // モバイルでは最小限の監視のみ
    if (isMobile) {
      // モバイルでは動画の再生状態を管理（X/Twitter風）
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 10%以上見えたら再生、完全に見えなくなったら停止
            const nowVisible = entry.intersectionRatio > 0.1;
            
            // 状態が変わった時のみ更新
            if (nowVisible !== isVisible) {
              setIsVisible(nowVisible);
            }
          });
        },
        {
          threshold: [0, 0.1, 0.5, 1.0], // より細かく監視
          rootMargin: '0px',
        }
      );

      const currentRef = containerRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }

    // PC向けの従来の設定
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visibility = entry.intersectionRatio;
          const nowVisible = visibility > 0.5;
          
          if (nowVisible !== isVisible) {
            setIsVisible(nowVisible);
          }
          
          if (visibility < 0.1 && activeVideoId === videoId) {
            pauseVideo(videoId);
          }
        });
      },
      {
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1.0],
        rootMargin: '-16px',
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [videoId, activeVideoId, pauseVideo, isVisible, isMobile]);

  // 自動再生ロジック
  useEffect(() => {
    if (!videoRef.current) return;

    // ホバー中の動画が優先（PCのみ）
    if (!isMobile && hoveredVideoId && hoveredVideoId !== videoId) {
      if (activeVideoId === videoId) {
        pauseVideo(videoId);
      }
      return;
    }

    // iPhoneで初回インタラクション前は自動再生しない
    if (isIPhone && !hasUserInteracted) {
      return;
    }

    // ホバー中、または可視状態で再生
    if ((isHovered || (isVisible && !hoveredVideoId)) && !pausedByClick) {
      const delay = isMobile ? 100 : 50; // モバイルも短い遅延でクイック再生
      const playTimer = setTimeout(() => {
        // モバイルでは個別の音声設定を使用
        const enableAudio = isMobile ? !videoMuted : isAudioEnabled;
        console.log('[Auto-play] Attempting to play video:', { videoId, isMobile, isVisible, enableAudio, videoMuted });
        playVideo(videoId, enableAudio);
      }, delay);
      return () => clearTimeout(playTimer);
    } else {
      // 非表示になった、またはpausedByClickの場合は停止
      if (activeVideoId === videoId) {
        pauseVideo(videoId);
      }
    }
  }, [isVisible, isHovered, hoveredVideoId, pausedByClick, isAudioEnabled, videoId, activeVideoId, playVideo, pauseVideo, isMobile, hasUserInteracted, isIPhone, videoMuted]);

  // 音声状態の変更を反映（VideoManagerと競合しないように削除）
  // VideoManagerのplayVideoがmuted属性を管理するため、ここでは設定しない

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setVideoHovered(videoId, true);
    setPausedByClick(false);
  }, [videoId, setVideoHovered]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setVideoHovered(videoId, false);
  }, [videoId, setVideoHovered]);

  const handleVideoClick = useCallback((event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    // ユーザーインタラクションを記録（アイコンからではなく動画を直接クリックした場合）
    if (!hasUserInteracted) {
      console.log('[User Interaction] First tap detected from video element');
      setHasUserInteracted(true);
      // 初回は必ずミュートで再生（iOSポリシー対応）
      playVideo(videoId, false);
      setPausedByClick(false);
      return;
    }

    // 2回目以降のクリックで再生/停止を制御
    if (video.paused) {
      // 再生（モバイルでは個別の音声設定を使用）
      const enableAudio = isMobile ? !videoMuted : isAudioEnabled;
      console.log('[handleVideoClick] Playing with audio:', enableAudio, 'videoMuted:', videoMuted);
      
      playVideo(videoId, enableAudio);
      setPausedByClick(false);
    } else {
      // 停止
      pauseVideo(videoId);
      setPausedByClick(true);
    }
  }, [videoId, isAudioEnabled, playVideo, pauseVideo, isMobile, hasUserInteracted, videoMuted]);

  const handleRestart = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    
    // モバイルでは個別の音声設定（videoMuted）を使用
    const enableAudio = isMobile ? !videoMuted : isAudioEnabled;
    console.log('[handleRestart] Playing with audio:', enableAudio, 'videoMuted:', videoMuted);
    
    playVideo(videoId, enableAudio);
    setPausedByClick(false);
  }, [videoId, isAudioEnabled, playVideo, isMobile, videoMuted]);

  const handleRewind = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, video.currentTime - 10);
  }, []);

  const handleForward = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(video.duration || 0, video.currentTime + 10);
  }, []);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // モバイルでは個別の音声設定（videoMuted）を使用
    const enableAudio = isMobile ? !videoMuted : isAudioEnabled;
    console.log('[handlePlay] Playing with audio:', enableAudio, 'videoMuted:', videoMuted);
    
    // VideoManagerのplayVideoに音声設定を委譲（video.mutedは設定しない）
    playVideo(videoId, enableAudio);
    setPausedByClick(false);
  }, [videoId, isAudioEnabled, playVideo, isMobile, videoMuted]);

  const handleVideoDoubleClick = useCallback(() => {
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.currentTime = 0;
      playVideo(videoId, isAudioEnabled);
    }
  }, [videoId, isAudioEnabled, playVideo]);

  // 個別の音声トグル
  const handleAudioToggle = useCallback((event) => {
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    const newMutedState = !videoMuted;
    console.log('[handleAudioToggle] Toggling audio:', { oldMuted: videoMuted, newMuted: newMutedState });
    setVideoMuted(newMutedState);
    
    // 再生中の場合は即座に反映
    if (!video.paused) {
      video.muted = newMutedState;
    }
    // 停止中の場合は、次回再生時にvideoMuted状態が使用される
  }, [videoMuted]);

  // styled wrapperがある場合はそれを、ない場合はデフォルトを使用
  const CardWrapper = StyledWrapper || (size === "full" ? FullCardStyled : size === "half" ? HalfCardStyled : HalfCardGrayStyled);

  return (
    <>
      {size === "full" && (
        <CardWrapper ref={containerRef}>
          <h3 className="heading-spacing-1824 u-pb8 u-pl16">{title}</h3>
          <p className={`u-w100 u-wrapper u-pb16 u-pl16 u-pr16 u-line-height-150 u-text-justify ${!isMobile ? 'slideup' : ''}`}>
            {children}
          </p>
          {isMobile ? (
            <div 
              className="u-col-center u-w100 u-relative"
              style={{ 
                paddingBottom: '56.25%', // 16:9のアスペクト比
                borderRadius: '0px 0px 12px 12px',
                overflow: 'hidden',
                cursor: 'pointer'
              }}

            >

              
              {/* プレースホルダー（初回タップ促進） - iPhoneのみ */}
              {!hasUserInteracted && isIPhone && (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[Initial Icon] Clicked');
                    const video = videoRef.current;
                    if (video) {
                      setHasUserInteracted(true);
                      setPausedByClick(false);
                      // 動画のmuted属性を確実に設定してから再生
                      video.muted = true;
                      setVideoMuted(true);
                      
                      // 直接play()を呼び出してから、VideoManagerに登録
                      video.play().then(() => {
                        console.log('[Initial Icon] Video started playing');
                        playVideo(videoId, false);
                      }).catch(err => {
                        console.error('[Initial Icon] Failed to play:', err);
                        // エラーが発生してもplayVideoを呼び出す
                        playVideo(videoId, false);
                      });
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '96px',
                    height: '96px',
                    display: 'flex',
                    alignItems: 'center',
                                          justifyContent: 'center',
                      zIndex: 10,
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 1px 3px rgba(255, 255, 255, 0.2))',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      WebkitTouchCallout: 'none'
                  }}>
                  <span 
                    className="material-symbols-rounded"
                    style={{
                      color: 'var(--soremo-border)',
                      fontSize: '96px',
                    }}
                  >
                    play_circle
                  </span>
                </div>
              )}
              
              <video
                ref={videoRef}
                src={isHls ? undefined : src}
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '0px 0px 12px 12px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none'
                }}
                playsInline
                muted
                controls={false}
                preload="auto"
                onClick={handleVideoClick}
                onLoadStart={(e) => {
                  console.log('[Mobile] Video load started:', {
                    src: e.target.src,
                    networkState: e.target.networkState
                  });
                }}
                onLoadedMetadata={(e) => {
                  console.log('[Mobile] Video metadata loaded:', {
                    src: e.target.src,
                    duration: e.target.duration,
                    videoWidth: e.target.videoWidth,
                    videoHeight: e.target.videoHeight,
                    readyState: e.target.readyState,
                    networkState: e.target.networkState,
                    paused: e.target.paused,
                    autoplay: e.target.autoplay
                  });
                  

                  // iOSでの自動再生を確実にする（metadataロード後）
                  if (isVisible && !pausedByClick) {
                    // 少し遅延を入れてから再生
                    setTimeout(() => {
                      if (e.target.paused && isVisible) {
                        e.target.play().catch(err => {
                          console.log('[Mobile] Auto-play attempt failed:', err);
                        });
                      }
                    }, 100);
                  }
                }}
                onError={(e) => {
                  console.error('[Mobile] Video error:', {
                    error: e.target.error,
                    errorCode: e.target.error?.code,
                    errorMessage: e.target.error?.message,
                    src: e.target.src,
                    currentSrc: e.target.currentSrc,
                    networkState: e.target.networkState,
                    readyState: e.target.readyState
                  });

                }}
                onCanPlay={(e) => {
                  console.log('[Mobile] Video can play:', {
                    src: e.target.src,
                    paused: e.target.paused,
                    isVisible
                  });

                  // 動画が再生可能になったら自動再生
                  if (isVisible && !pausedByClick && e.target.paused) {
                    e.target.play().catch(err => {
                      console.log('[Mobile] Play on canplay failed:', err);
                    });
                  }
                }}
                onWaiting={() => {
                  console.log('[Mobile] Video waiting for data');
                }}
              />
              {pausedByClick && (
                <>
                  {/* モバイル用のコントロール */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    gap: '40px',
                    alignItems: 'center',
                    zIndex: 5
                  }}>
                    {/* 巻き戻し */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleRewind(); }}
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'screen',
                          filter: 'drop-shadow(0 1px 3px rgba(255, 255, 255, 0.2))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: 'var(--soremo-border)', 
                        fontSize: '32px'
                      }}>
                        replay_10
                      </span>
                    </div>
                    {/* 再生 */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handlePlay(); }}
                      style={{
                        width: '96px',
                        height: '96px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'screen',
                          filter: 'drop-shadow(0 1px 3px rgba(255, 255, 255, 0.2))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: 'var(--soremo-border)', 
                        fontSize: '96px',
                        textShadow: '0 0 25px rgba(255, 255, 255, 0.8)'
                      }}>
                        play_circle
                      </span>
                    </div>
                    {/* 早送り */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleForward(); }}
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'screen',
                          filter: 'drop-shadow(0 1px 3px rgba(255, 255, 255, 0.2))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: 'var(--soremo-border)', 
                        fontSize: '32px'
                      }}>
                        forward_10
                      </span>
                    </div>
                  </div>
                  {/* 最初から再生ボタン */}
                  <IconContainer>
                    <IconButton onClick={handleRestart}>
                      <div className="u-row u-gap4">
                        replay
                        <span className="bodytext-13 u-line-height-100">
                          最初から再生
                        </span>
                      </div>
                    </IconButton>
                  </IconContainer>
                </>
              )}
              {/* モバイルでのみ音声コントロールを表示 */}
              {isMobile && (
                <AudioControlContainer onClick={handleAudioToggle}>
                  <AudioIcon>{videoMuted ? 'volume_off' : 'volume_up'}</AudioIcon>
                </AudioControlContainer>
              )}
            </div>
          ) : (
            <motion.div 
              className="u-col-center u-w100 u-relative"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <VideoStyled
                ref={videoRef}
                src={isHls ? undefined : src}
                controlsList="nodownload"
                playsInline
                preload="auto"
                muted={!isAudioEnabled}
                onClick={handleVideoClick}
                onDoubleClick={handleVideoDoubleClick}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1.5 }}
                viewport={{ once: true, amount: 1 }}
              />
              {pausedByClick && (
                <IconContainer>
                  <IconButton onClick={handleRestart}>
                    <div className="u-row u-gap4">
                      replay
                      <span className="bodytext-13 u-line-height-100">
                        最初から再生
                      </span>
                    </div>
                  </IconButton>
                </IconContainer>
              )}
            </motion.div>
          )}
        </CardWrapper>
      )}
      {size === "half" && (
        <CardWrapper ref={containerRef}>
          <h3 className="heading-spacing-1824 u-pb8 u-pl16">{title}</h3>
          {isMobile ? (
            <p className="u-w100 u-wrapper u-pb16 u-pl16 u-pr16 u-line-height-150 u-text-justify slideup">
              {children}
            </p>
          ) : (
            <motion.p
              className="u-w100 u-wrapper u-pb16 u-pl16 u-pr16 u-line-height-150 u-text-justify slideup"
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, amount: 1 }}
            >
              {children}
            </motion.p>
          )}
          {isMobile ? (
            <div 
              className="u-col-center u-w100 u-relative"
              style={{ 
                paddingBottom: '56.25%', // 16:9のアスペクト比
                borderRadius: '0px 0px 12px 12px',
                overflow: 'hidden',
                cursor: 'pointer'
              }}

            >

              
              {/* プレースホルダー（初回タップ促進） - iPhoneのみ */}
              {!hasUserInteracted && isIPhone && (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[Initial Icon] Clicked');
                    const video = videoRef.current;
                    if (video) {
                      setHasUserInteracted(true);
                      setPausedByClick(false);
                      // 動画のmuted属性を確実に設定してから再生
                      video.muted = true;
                      setVideoMuted(true);
                      
                      // 直接play()を呼び出してから、VideoManagerに登録
                      video.play().then(() => {
                        console.log('[Initial Icon] Video started playing');
                        playVideo(videoId, false);
                      }).catch(err => {
                        console.error('[Initial Icon] Failed to play:', err);
                        // エラーが発生してもplayVideoを呼び出す
                        playVideo(videoId, false);
                      });
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '72px',
                    height: '72px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                                              cursor: 'pointer',
                          mixBlendMode: 'exclusion',
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                  }}>
                  <span 
                    className="material-symbols-rounded"
                    style={{
                      color: 'white',
                      fontSize: '72px',
                      fontWeight: '400'
                    }}
                  >
                    play_circle
                  </span>
                </div>
              )}
              
              <video
                ref={videoRef}
                src={isHls ? undefined : src}
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '0px 0px 12px 12px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none'
                }}
                playsInline
                muted
                controls={false}
                preload="auto"
                onClick={handleVideoClick}
                onLoadStart={(e) => {
                  console.log('[Mobile] Video load started:', {
                    src: e.target.src,
                    networkState: e.target.networkState
                  });
                }}
                onLoadedMetadata={(e) => {
                  console.log('[Mobile] Video metadata loaded:', {
                    src: e.target.src,
                    duration: e.target.duration,
                    videoWidth: e.target.videoWidth,
                    videoHeight: e.target.videoHeight,
                    readyState: e.target.readyState,
                    networkState: e.target.networkState,
                    paused: e.target.paused,
                    autoplay: e.target.autoplay
                  });
                  

                  // iOSでの自動再生を確実にする（metadataロード後）
                  if (isVisible && !pausedByClick) {
                    // 少し遅延を入れてから再生
                    setTimeout(() => {
                      if (e.target.paused && isVisible) {
                        e.target.play().catch(err => {
                          console.log('[Mobile] Auto-play attempt failed:', err);
                        });
                      }
                    }, 100);
                  }
                }}
                onError={(e) => {
                  console.error('[Mobile] Video error:', {
                    error: e.target.error,
                    errorCode: e.target.error?.code,
                    errorMessage: e.target.error?.message,
                    src: e.target.src,
                    currentSrc: e.target.currentSrc,
                    networkState: e.target.networkState,
                    readyState: e.target.readyState
                  });

                }}
                onCanPlay={(e) => {
                  console.log('[Mobile] Video can play:', {
                    src: e.target.src,
                    paused: e.target.paused,
                    isVisible
                  });

                  // 動画が再生可能になったら自動再生
                  if (isVisible && !pausedByClick && e.target.paused) {
                    e.target.play().catch(err => {
                      console.log('[Mobile] Play on canplay failed:', err);
                    });
                  }
                }}
                onWaiting={() => {
                  console.log('[Mobile] Video waiting for data');
                }}
              />
              {pausedByClick && (
                <>
                  {/* モバイル用のコントロール */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    gap: '40px',
                    alignItems: 'center',
                    zIndex: 5
                  }}>
                    {/* 巻き戻し */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleRewind(); }}
                      style={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'exclusion',
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: 'white', 
                        fontSize: '24px',
                      }}>
                        replay_10
                      </span>
                    </div>
                    {/* 再生 */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handlePlay(); }}
                      style={{
                        width: '72px',
                        height: '72px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'exclusion',
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: '#a7a8a9', 
                        fontSize: '72px',
                        textShadow: '0 0 25px rgba(255, 255, 255, 0.8)'
                      }}>
                        play_circle
                      </span>
                    </div>
                    {/* 早送り */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleForward(); }}
                      style={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                                                  cursor: 'pointer',
                          mixBlendMode: 'exclusion',
                          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5))',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none'
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ 
                        color: 'white', 
                        fontSize: '24px',
                      }}>
                        forward_10
                      </span>
                    </div>
                  </div>
                  {/* 最初から再生ボタン */}
                  <IconContainer>
                    <IconButton onClick={handleRestart}>
                      <div className="u-row u-gap4">
                        replay
                        <span className="bodytext-11 u-line-height-100">
                          最初から再生
                        </span>
                      </div>
                    </IconButton>
                  </IconContainer>
                </>
              )}
              {/* モバイルでのみ音声コントロールを表示 */}
              {isMobile && (
                <AudioControlContainer onClick={handleAudioToggle}>
                  <AudioIcon>{videoMuted ? 'volume_off' : 'volume_up'}</AudioIcon>
                </AudioControlContainer>
              )}
            </div>
          ) : (
            <motion.div 
              className="u-col-center u-w100 u-relative"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <VideoStyled
                ref={videoRef}
                src={isHls ? undefined : src}
                controlsList="nodownload"
                playsInline
                preload="auto"
                muted={!isAudioEnabled}
                onClick={handleVideoClick}
                onDoubleClick={handleVideoDoubleClick}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1.5 }}
                viewport={{ once: true, amount: 1 }}
              />
              {pausedByClick && (
                <IconContainer>
                  <IconButton onClick={handleRestart}>
                    <div className="u-row u-gap4">
                      replay
                      <span className="bodytext-11 u-line-height-100">
                        最初から再生
                      </span>
                    </div>
                  </IconButton>
                </IconContainer>
              )}
            </motion.div>
          )}
        </CardWrapper>
      )}
      {size === "halfgray" && (
        <CardWrapper>
          <h3 className="heading-spacing-1824 u-pb8 u-pl16">{title}</h3>
          <motion.img 
            className="u-w100" 
            src={src} 
            alt={title}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          />
          <p className="u-w100 u-wrapper-reading u-pb16 u-pl16 u-pr16 u-line-height-150 u-text-justify slideup">
            {children}
          </p>
        </CardWrapper>
      )}
    </>
  );
};

export default OsCard;